import {
  FormItem,
  Feedback,
  Icon,
  Text,
  TextArea,
} from '@cubeyou-team/hx-react-components';
import { AI_GRADIENT_PROPERTIES } from '@discover-frontend/constants';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback } from 'react';
import { useController, useForm } from 'react-hook-form';
import theme from '@cubeyou-team/hx-theme';

import translations from 'src/lang';
import { StanleyQueryRequestPayload } from '../../../../../../api/types';
import { useStanleyQueryPanel } from '../../../../providers/StanleyQueryPanelContext';
import { stanleyQueryPanelContainerSchema } from './schema';
import { StanleyQueryFooter } from '../stanley-query-footer';

const { STANLEY_QUERY: translation } = translations['en-US'];

export const StanleyQueryFormContainer = () => {
  const {
    isLoading,
    lastSubmittedPrompt,
    handlePostStanleyQuery,
    isCreditsExpired,
  } = useStanleyQueryPanel();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<StanleyQueryRequestPayload>({
    mode: 'onSubmit',
    resolver: yupResolver(stanleyQueryPanelContainerSchema),
    defaultValues: {
      user_prompt: lastSubmittedPrompt,
    },
  });
  const { field: promptField } = useController({
    name: 'user_prompt',
    control,
  });

  const onSubmit = useCallback(
    (payload: StanleyQueryRequestPayload) => handlePostStanleyQuery(payload),
    [handlePostStanleyQuery]
  );

  const handlePromptKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      e.preventDefault();
      if (isLoading || isCreditsExpired) return;
      handleSubmit(onSubmit)(e);
    },
    [isCreditsExpired, isLoading, handleSubmit, onSubmit]
  );

  return (
    <>
      {isCreditsExpired && (
        <div className="mb-4">
          <Feedback
            status="warning"
            message={translation.MODAL.CREDITS_EXPIRED.MESSAGE}
            closable
          />
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-2 h-1/2 max-w-sm mx-auto">
        <Icon icon="icon-ai" size="xxlarge" gradient={AI_GRADIENT_PROPERTIES} />
        <Text size="headline" align="center">
          {translation.MODAL.DESCRIPTION}
        </Text>
      </div>
      <div className="flex flex-col">
        <FormItem error={!!errors.user_prompt}>
          <TextArea
            placeholder={translation.MODAL.PLACEHOLDER}
            value={promptField.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              promptField.onChange(e.target.value)
            }
            onBlur={promptField.onBlur}
            onKeyDown={handlePromptKeyDown}
            style={{
              height: '340px',
            }}
          />
          <div className="flex items-center gap-2">
            <Icon icon="informations" size="small" />
            <Text size="small">{translation.MODAL.HELP_TEXT}</Text>
          </div>
        </FormItem>
        {errors.user_prompt?.message && (
          <Text
            size="small"
            variant="error"
            className="-mt-2 mb-4"
            role="alert"
          >
            {errors.user_prompt.message}
          </Text>
        )}

        <div
          style={{
            borderTop: `1px solid ${theme.colors.light_darker}`,
          }}
        >
          <StanleyQueryFooter
            isLoading={isLoading}
            isNewQuery={false}
            onPrimaryClick={handleSubmit(onSubmit)}
          />
        </div>
      </div>
    </>
  );
};
